import {get} from 'svelte/store';
import {BehaviorSubject, Subject, combineLatest, startWith, shareReplay} from 'rxjs';
import {switchMap, skipUntil, filter, map, tap} from 'rxjs/operators';
import {mc} from '@/core';
import {announcementApi} from '@/api/announcement';
import {accountApi} from '@/api/account';
import {activityApi} from '@/api/activity';
import {browser$} from '@/streams/browser';
import {userStatus$} from '@/streams/auth';
import {lendingGeneralPools$, lendingGeneralPoolsOverview$} from '@/modules/web3/lending/streams/pool';
import {contractMarkets$, spotMarkets$} from '@/modules/markets/streams-market';
import {TradeEarnEnums} from '@/modules/homepage/constants';
import {numUtil} from '@/helper';
import {strategyApi} from '@/api/strategy';
import {isLogin$} from '@/streams/auth';
import {lang$} from '@/store';
import {currencies$} from '@/streams/config/currency';
import Big from 'big.js';

const language = get(lang$);

export const query$ = new Subject();
export const tasks$ = new BehaviorSubject([]);
export const hotSpots$ = new BehaviorSubject([]);
export const hotContracts$ = new BehaviorSubject([]);
const tradingEarnings$ = new BehaviorSubject(0);
const botsWorldwide$ = new BehaviorSubject(0);

export const bannerList$ = combineLatest([query$, userStatus$]).pipe(
  skipUntil(browser$),
  filter(([, status]) => status !== 'Init'),
  switchMap(([query, status]) => loadBannerList(query, status)),
  startWith({loading: true, list: []}),
  shareReplay(1)
);

async function loadBannerList(query, status) {
  const {data, error} = await (status === 'Login' ? announcementApi.contentViewLogin(query) : announcementApi.contentViewLogout(query));
  if (data) {
    return {loading: false, list: data.rows};
  }
  if (error) {
    mc.send('toast', 'error', error);
  }
  return {loading: false, list: []};
}

browser$.subscribe(async () => {
  const {data} = await activityApi.getHotSpotsAndContracts();
  hotSpots$.next(data.spot);
  hotContracts$.next(data.contract);
});

export async function loadTasksList() {
  const {data, error} = await accountApi.getTasksList();
  if (data) {
    return tasks$.next(data.rows);
  }
  if (error) {
    mc.send('toast', 'error', error);
  }
  return [];
}

// 数组求和
const arraySumByKeys = (dataSet = [], key, currencies) => {
  return dataSet.reduce(
    (accr, curr) => {
      const cry = curr.quoteCurrency || 'USDT';
      const {valuePrecision = 8, valueScale = 8} = currencies.find((o) => o.currency === cry) || {};
      return {[key]: accr[key].plus(numUtil.floor(curr[key] || 0, valuePrecision, valueScale))};
    },
    {[key]: Big(0)}
  )[key];
};

const arraySumByKeysForContract = (dataSet = []) => {
  return dataSet.reduce((accr, curr) => {
    const KEY = curr.contractSide > 0 ? 'turnoverEv' : 'volume';
    return accr.plus(curr[KEY] || 0);
  }, Big(0));
};

const convertMarketVal = (options) => {
  const newResult = [...TradeEarnEnums];
  options.forEach(({sum = 0, supplyAPY, totalAvailable, earn, wide, length, key}) => {
    const idx = newResult.findIndex((item) => item.key === key);
    if (idx >= 0) {
      const vals = {
        FT: {
          firstVal: '$' + numUtil.abbreviateNumber(sum),
          secondVal: length,
          pairsNum: length,
        },
        SM: {
          firstVal: '$' + numUtil.abbreviateNumber(sum),
          secondVal: length,
          pairsNum: length,
        },
        PLP: {
          firstVal: supplyAPY,
          secondVal: '$' + totalAvailable,
        },
        TB: {
          firstVal: '$' + numUtil.delimit(earn),
          secondVal: numUtil.delimit(wide),
        },
      };
      newResult[idx] = {
        ...newResult[idx],
        ...vals[key],
      };
    }
  });
  return newResult;
};

export async function getStrategySummer() {
  const {data, error} = await strategyApi.marketplaceSummary();
  if (data) {
    const {strategyCnt, strategyEarning} = data;
    tradingEarnings$.next(strategyEarning);
    botsWorldwide$.next(strategyCnt);
  }
  if (error) {
    mc.send('toast', 'error', error);
  }
}

export const TradeEarnVals$ = combineLatest([contractMarkets$, spotMarkets$, lendingGeneralPools$, lendingGeneralPoolsOverview$, tradingEarnings$, botsWorldwide$, currencies$]).pipe(
  map(([contractMarkets = [], spotMarkets = [], lendingGeneralPools = [], lendingOverview = {}, tradingEarnings = '0', botsWorldwide = '0', currencies]) => {
    const contractVolSum = arraySumByKeysForContract(contractMarkets);
    const spotVolSum = arraySumByKeys(spotMarkets, 'turnoverEv', currencies);
    const validLendingList = lendingGeneralPools.filter((o) => !!o.canBorrow || !!o.canSupply);
    // 列表中取APY最大的一个数据
    const lendingTarget = validLendingList.reduce((max, curr) => {
      return curr.supplyAPYRr > max.supplyAPYRr ? curr : max;
    }, validLendingList[0]);
    return convertMarketVal([
      {sum: contractVolSum, length: contractMarkets.length, key: 'FT'},
      {sum: spotVolSum, length: spotMarkets.length, key: 'SM'},
      {earn: tradingEarnings, wide: botsWorldwide, key: 'TB'},
      {supplyAPY: lendingTarget?.supplyAPY, totalAvailable: lendingOverview.totalAvailable, key: 'PLP'},
    ]);
  }),
  startWith(TradeEarnEnums),
  shareReplay(1)
);

async function getBannerConfig(isLogin) {
  const {data: {rows = []} = {}} = await announcementApi.getBannerList(isLogin, {
    device: 'WEB',
    itemKey: 'banner',
    lang: language,
    location: 23,
  });
  if (!!rows.length) {
    // 后端坐过排序，取id最大的活动即可
    const maxIdItem = rows.reduce((maxItem, currentItem) => {
      return currentItem.id > maxItem.id ? currentItem : maxItem;
    });
    return {
      loading: false,
      info: maxIdItem.detail || {},
    };
  }
  return {
    loading: false,
    info: {},
  };
}

export const bannerConfig$ = isLogin$.pipe(
  switchMap((isLogin) => getBannerConfig(isLogin)),
  startWith({
    loading: true,
    info: {
      titleTextColors: ['#01DFFF', '#017BFF'],
      title: '',
      description: '',
    },
  }),
  shareReplay(1)
);
